/**
 * The start view's listbox (Option C, C1, prototype `look/start.png`): engine cards grouped as Databases and Azure
 * services, then the "Other ways to add" rows. Every card and row is a `role="option"` of ONE listbox that the filter
 * (the combobox) controls; focus never leaves the filter (a pointer press does not move it), a click activates.
 * Cards keep `engine-tile-<id>` and the list keeps `engine-tile-selector`; the rows keep today's entry ids.
 *
 * C1b: every card, row and caption is a flip item (`data-flip-key`, the same keys as the rail's) whose visible parts are
 * sibling layers (`data-flip-layer`): the surface (border, background, the active ring) behind the icon, label and the
 * card-only parts (blurb, chevron), so the transition can move each one (`transition/flipPlan.ts`).
 */
import { forwardRef, type HTMLAttributes, type ReactNode } from 'react';
import { ChevronRight } from 'lucide-react';
import { cn } from '@/lib/utils';
import { ImportSourcesMenu } from '../../connection-import';
import { ConnectionEngineIcon } from '../../components/ConnectionEngineIcon';
import type { EngineCatalogEntry } from '../state/engineCatalog';
import type { EntryPointsApi } from '../hooks/useEntryPoints';
import { ENTRY_POINTS, type EntryPointInfo } from './entryPoints';
import { engineOptionId, entryOptionId, type StartOptionId } from './engineFilter';
import { startOptionDomId } from './useEngineCombobox';

type Props = {
  databases: readonly EngineCatalogEntry[];
  azure: readonly EngineCatalogEntry[];
  active: StartOptionId | null;
  onActivate: (id: StartOptionId) => void;
  entries: EntryPointsApi;
  /** Focus goes back to the filter when the import menu closes. */
  onMenuClosed: () => void;
};

/** A transparent border keeps the content where the box's border puts it. */
const OPTION = 'group relative border border-transparent text-left outline-none cursor-pointer';
/**
 * The option's box: a layer of its own, so its size can change without stretching the text (over the wrapper's border).
 * Hover tints the background only; the keyboard's active option gets the tint AND a 2-px ring, so the two never look
 * alike when a mouse rests on the grid (final UX review UX-07).
 */
const SURFACE = 'absolute -inset-px rounded-lg border bg-background transition-[background-color,border-color,box-shadow] group-hover:bg-muted/60';
const ACTIVE = 'border-ring bg-primary/5 ring-2 ring-ring group-hover:bg-primary/5';
/** A press on an option keeps focus in the filter (the combobox pattern). */
const keepFocus = (event: React.MouseEvent) => event.preventDefault();

function Group({ id, caption, children }: { id: string; caption: string; children: ReactNode }) {
  return (
    <div role="group" aria-labelledby={`connection-start-group-${id}`} className="mb-4 last:mb-0">
      <div id={`connection-start-group-${id}`} className="mb-2 flex items-center gap-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
        <span data-flip-key={`caption:${id}`} data-flip-layer="label" className="relative">
          {caption}
        </span>
        <span aria-hidden data-flip-chrome="fade" className="h-px flex-1 bg-border" />
      </div>
      {children}
    </div>
  );
}

function EngineCard({ entry, active, onActivate }: { entry: EngineCatalogEntry; active: boolean; onActivate: () => void }) {
  const id = engineOptionId(entry.id);
  return (
    <div
      role="option"
      id={startOptionDomId(id)}
      aria-selected={active}
      data-testid={`engine-tile-${entry.id}`}
      data-flip-key={id}
      onMouseDown={keepFocus}
      onClick={onActivate}
      className={cn(OPTION, 'flex min-h-[66px] items-start gap-3 p-3')}
    >
      <span aria-hidden data-flip-layer="surface" className={cn(SURFACE, 'border-input shadow-xs', active && ACTIVE)} />
      <span data-flip-layer="icon" className="relative flex shrink-0">
        <ConnectionEngineIcon engine={entry.id} iconOnly size="lg" className="h-7 w-7" />
      </span>
      <span className="relative flex min-w-0 flex-col gap-0.5">
        <span data-flip-layer="label" className="text-sm font-medium leading-tight">
          {entry.label}
        </span>
        <span data-flip-layer="blurb" className="text-xs leading-snug text-muted-foreground">
          {entry.blurb}
        </span>
      </span>
    </div>
  );
}

type EntryRowProps = HTMLAttributes<HTMLDivElement> & { entry: EntryPointInfo; active: boolean; reason?: string; onActivate?: () => void };

/**
 * Forwards its ref and the trigger props Radix passes when the row opens the import source menu — except `aria-expanded`
 * and `aria-haspopup`, which a `role="option"` may not carry (axe aria-allowed-attr, critical). The filter is the
 * combobox that owns the listbox; the menu takes focus when it opens.
 */
const EntryRow = forwardRef<HTMLDivElement, EntryRowProps>(function EntryRow(
  { entry, active, reason, onActivate, 'aria-expanded': _expanded, 'aria-haspopup': _haspopup, ...trigger },
  ref,
) {
  const id = entryOptionId(entry.id);
  return (
    <div
      {...trigger}
      ref={ref}
      role="option"
      id={startOptionDomId(id)}
      aria-selected={active}
      aria-disabled={reason ? true : undefined}
      title={reason}
      data-disabled-reason={reason}
      data-testid={entry.testId}
      data-flip-key={id}
      onMouseDown={keepFocus}
      onClick={onActivate}
      className={cn(OPTION, 'flex min-h-[52px] items-center gap-3 px-3 py-2', reason && 'cursor-not-allowed opacity-60')}
    >
      <span aria-hidden data-flip-layer="surface" className={cn(SURFACE, 'border-border', active && ACTIVE)} />
      <span data-flip-layer="icon" className="relative flex shrink-0">
        <entry.Icon className="h-[18px] w-[18px] text-primary" aria-hidden />
      </span>
      <span className="relative flex min-w-0 flex-1 flex-col">
        <span data-flip-layer="label" className="text-sm font-medium leading-tight">
          {entry.label}
        </span>
        <span data-flip-layer="blurb" className="truncate text-xs text-muted-foreground">
          {reason ?? entry.description}
        </span>
      </span>
      <span data-flip-layer="chevron" className="relative flex shrink-0">
        <ChevronRight className="h-3.5 w-3.5 text-muted-foreground" aria-hidden />
      </span>
    </div>
  );
});

export function EngineOptionList({ databases, azure, active, onActivate, entries, onMenuClosed }: Props) {
  const cards = (list: readonly EngineCatalogEntry[]) => (
    <div className="grid grid-cols-[repeat(auto-fill,minmax(14rem,1fr))] gap-2">
      {list.map((entry) => (
        <EngineCard key={entry.id} entry={entry} active={active === engineOptionId(entry.id)} onActivate={() => onActivate(engineOptionId(entry.id))} />
      ))}
    </div>
  );
  return (
    <div role="listbox" id="connection-start-options" aria-label="Engines and other ways to add" data-testid="engine-tile-selector">
      {databases.length > 0 && <Group id="databases" caption="Databases">{cards(databases)}</Group>}
      {azure.length > 0 && <Group id="azure" caption="Azure services">{cards(azure)}</Group>}
      <Group id="other" caption="Other ways to add">
        <div className="grid grid-cols-[repeat(auto-fill,minmax(18rem,1fr))] gap-2">
          {ENTRY_POINTS.map((entry) => {
            const isActive = active === entryOptionId(entry.id);
            const reason = entries.reasonFor(entry.id);
            if (entry.id !== 'import' || reason) {
              return <EntryRow key={entry.id} entry={entry} active={isActive} reason={reason} onActivate={() => onActivate(entryOptionId(entry.id))} />;
            }
            // The row is the source menu's trigger: a press opens it (Radix), Enter in the filter opens it too.
            return (
              <ImportSourcesMenu
                key={entry.id}
                entry="connection_dialog"
                placement="start"
                open={entries.importMenuAt === 'start'}
                onOpenChange={(open) => (open ? entries.activate('import', 'start') : entries.closeImportMenu())}
                onCloseAutoFocus={(event) => {
                  event.preventDefault();
                  onMenuClosed();
                }}
                trigger={<EntryRow entry={entry} active={isActive} />}
              />
            );
          })}
        </div>
      </Group>
    </div>
  );
}
